import React, {useMemo} from 'react';
import {type FieldPath, type FieldValues, useController} from 'react-hook-form';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '@netx/core-components/Form';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {combineRefs} from '@netx/core-next/utils/react';
import {type IGroupAutoListItem} from '../../schema';
import {GroupAutoList, type GroupAutoListProps} from './GroupAutoList';
import {legacyPermissionsEnabled} from '@netx/core-permissions-next/enablers';

export interface ReactHookFormGroupAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
	GroupAutoListProps<Multiple, DisableClearable>,
	TFieldValues,
	TName
> {
	valueLabelName?: string;
}

/**
 * `react-hook-form` wrapper for `GroupAutoList`
 */
export const ReactHookFormGroupAutoListPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		valueLabelName,
		ControllerProps,
		...props
	}: ReactHookFormGroupAutoListProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const fieldName = props.name || 'group-auto-list';
	valueLabelName = valueLabelName ?? `${fieldName}-displayLabel`;

	const valueLabelController = useController<TFieldValues, any>({
		control,
		name: valueLabelName,
		shouldUnregister: control._options.shouldUnregister,
		rules: ControllerProps?.rules,
	});

	const legacyPerms = legacyPermissionsEnabled();

	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={fieldName as TName}
			render={({field, formState}) => {
				const value = useMemo(
					() =>
						field.value != null
							? Array.isArray(field.value)
								? (field.value as IGroupAutoListItem[]).map((v, idx) => {
										return {
											id: v,
											label: valueLabelController.field.value[idx],
										};
									})
								: {id: field.value, label: valueLabelController.field.value}
							: props.multiple
								? []
								: (null as any),
					[field.value, valueLabelController.field.value],
				);

				const handleChange = useConstCallback(
					splitEventHandler(props.onChange, (event, value) => {
						if (Array.isArray(value)) {
							const everyoneIndex = valueLabelController.field.value.findIndex(
								(v: string) => v === 'Everyone',
							);
							const administratorsIndex =
								valueLabelController.field.value.findIndex(
									(v: string) => v === 'Administrators',
								);
							const data = (value as IGroupAutoListItem[]).reduce<{
								ids: IGroupAutoListItem['value'][];
								labels: IGroupAutoListItem['label'][];
							}>(
								(accum, v) => {
									accum.ids.push(v.value ?? (v as any).id);
									accum.labels.push(
										v.label ??
											valueLabelController.field.value[accum.ids.length - 1] ??
											'',
									);
									return accum;
								},
								{
									ids: [],
									labels: [],
								},
							);

							// Enforce "Everyone" to be selected and the first item
							// TODO: make this an opt in - right now only one place is using this particular component
							// as a multiselect
							if (
								everyoneIndex !== -1 &&
								!data.labels.includes('Everyone') &&
								legacyPerms &&
								administratorsIndex !== -1 &&
								!data.labels.includes('Administrators')
							) {
								data.ids.unshift(field.value[everyoneIndex]);
								data.labels.unshift('Everyone');
								data.ids.unshift(field.value[administratorsIndex]);
								data.labels.unshift('Administrators');
							} else {
								if (everyoneIndex !== -1 && !data.labels.includes('Everyone')) {
									if (administratorsIndex > -1) {
										data.ids.splice(
											administratorsIndex + 1,
											0,
											field.value[everyoneIndex],
										);
										data.labels.splice(administratorsIndex + 1, 0, 'Everyone');
									} else {
										data.ids.unshift(field.value[everyoneIndex]);
										data.labels.unshift('Everyone');
									}
								}
								if (
									legacyPerms &&
									administratorsIndex !== -1 &&
									!data.labels.includes('Administrators')
								) {
									if (everyoneIndex > -1) {
										data.ids.splice(
											everyoneIndex + 1,
											0,
											field.value[administratorsIndex],
										);
										data.labels.splice(everyoneIndex + 1, 0, 'Administrators');
									} else {
										data.ids.unshift(field.value[administratorsIndex]);
										data.labels.unshift('Administrators');
									}
								}
							}

							field.onChange(data.ids);
							valueLabelController.field.onChange(data.labels);
							return;
						}
						field.onChange((value as IGroupAutoListItem)?.value || 0);
						valueLabelController.field.onChange(
							(value as IGroupAutoListItem)?.label,
						);
					}),
				);

				return (
					<GroupAutoList<Multiple, DisableClearable>
						error={formState.errors[fieldName]?.message?.toString()}
						{...props}
						{...field}
						disabled={props.disabled || field.disabled}
						ref={combineRefs(ref, field.ref)}
						value={value}
						onChange={handleChange}
						getTagOptionLabel={(option) => {
							if (!props.multiple) return option.label;
							const index = field.value.indexOf((option as any).id);
							if (index === -1) return option.label;
							return valueLabelController.field.value[index];
						}}
					/>
				);
			}}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

export const ReactHookFormGroupAutoList = React.forwardRef(
	ReactHookFormGroupAutoListPrimitive,
) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormGroupAutoListProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
) => JSX.Element;

export default ReactHookFormGroupAutoList;
